"use client";

import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PopoverClose } from "@/components/ui/popover";
import { backendClient } from "@/lib/backend-api";
import { gettingStartedQueryKey } from "@/hooks/use-getting-started-progress";
import { useGettingStartedTasks } from "@/hooks/use-getting-started-tasks";
import {
  GETTING_STARTED_SECTIONS,
  GETTING_STARTED_TASKS_BY_ID,
  GettingStartedParentId,
  JURNI_CORE_TASK_ID,
  gettingStartedTaskHref,
  type GettingStartedTask,
} from "@/constants/getting-started";
import type { GettingStartedTaskId } from "@jurni/schemas";
import {
  GettingStartedSection,
  type GettingStartedSectionData,
} from "./getting-started-section";
import type { GettingStartedTaskStatus } from "./getting-started-task-row";
import { useRouter } from "nextjs-toploader/app";
import { usePathname } from "next/navigation";
import { useJurniCoreActivation } from "@/hooks/use-jurni-core-activation";

interface Props {
  onTaskNavigate: () => void;
}

const getCompletionPercentage = (completedCount: number, totalCount: number) =>
  totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

const isTaskDone = (status?: GettingStartedTaskStatus) =>
  !!(status?.completed || status?.dismissed);

const getCompletedTaskCount = (
  tasks: readonly { id: GettingStartedTaskId }[],
  taskStatusMap: Map<GettingStartedTaskId, GettingStartedTaskStatus>,
) => tasks.filter((task) => isTaskDone(taskStatusMap.get(task.id))).length;

const getSections = ({
  expandedSections,
  taskStatusMap,
  visibleTasks,
}: {
  expandedSections: Set<GettingStartedParentId>;
  taskStatusMap: Map<GettingStartedTaskId, GettingStartedTaskStatus>;
  visibleTasks: GettingStartedTask[];
}): GettingStartedSectionData[] =>
  GETTING_STARTED_SECTIONS.map((section) => {
    const tasks = visibleTasks.filter((task) => task.parentId === section.id);
    const completedTasks = getCompletedTaskCount(tasks, taskStatusMap);

    return {
      ...section,
      percentage: getCompletionPercentage(completedTasks, tasks.length),
      isCollapsed: !expandedSections.has(section.id),
      tasks,
    };
  });

export const GettingStartedPanel = ({ onTaskNavigate }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const [expandedSections, setExpandedSections] = useState<
    Set<GettingStartedParentId>
  >(new Set([GETTING_STARTED_SECTIONS[0]?.id]));
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (pendingNavigation !== null && pathname === pendingNavigation) {
      onTaskNavigate();
      setPendingNavigation(null);
    }
  }, [pathname, pendingNavigation, onTaskNavigate]);

  const queryClient = useQueryClient();

  const { mutate: dismissTask } = useMutation({
    mutationFn: ({
      taskId,
      dismissed,
    }: {
      taskId: GettingStartedTaskId;
      dismissed: boolean;
    }) => backendClient.shop.dismissGettingStartedTask({ taskId, dismissed }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: gettingStartedQueryKey }),
  });

  const { tasks } = useGettingStartedTasks();

  const taskStatusMap = new Map<GettingStartedTaskId, GettingStartedTaskStatus>(
    tasks.map((task) => [task.id, task]),
  );

  const visibleTasks = tasks.map((task) => ({
    id: task.id,
    ...GETTING_STARTED_TASKS_BY_ID[task.id],
  }));

  const sections = getSections({
    expandedSections,
    taskStatusMap,
    visibleTasks,
  });

  const completedCount = getCompletedTaskCount(visibleTasks, taskStatusMap);

  const overallPercentage = getCompletionPercentage(
    completedCount,
    visibleTasks.length,
  );

  const toggleSection = (sectionId: GettingStartedParentId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  };

  const activateJurniCore = useJurniCoreActivation();

  const handleTaskClick = (task: GettingStartedTask) => {
    // Some task links open a tab through the query string; navigation completes on the pathname.
    const taskPathname = new URL(task.href, window.location.origin).pathname;
    if (pathname === taskPathname) {
      onTaskNavigate();
    } else {
      setPendingNavigation(taskPathname);
    }
    router.push(gettingStartedTaskHref(task));
  };

  const handleDismissTask = (taskId: GettingStartedTaskId) => {
    dismissTask({
      taskId,
      dismissed: true,
    });
  };

  return (
    <div className="overflow-hidden pb-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[15px] font-medium tracking-[-0.15px]">
          Account Setup
        </span>
        <div className="flex items-center gap-3">
          <span className="text-[13px] tracking-[-0.13px] text-muted-foreground">
            {overallPercentage}% Complete
          </span>
          <PopoverClose asChild>
            <button className="flex size-5 items-center justify-center text-muted-foreground hover:text-foreground">
              <X size={14} />
            </button>
          </PopoverClose>
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-2">
        {sections.map((section) => (
          <GettingStartedSection
            key={section.id}
            section={section}
            taskStatusMap={taskStatusMap}
            onToggle={toggleSection}
            onTaskClick={(task) =>
              task.id === JURNI_CORE_TASK_ID
                ? activateJurniCore()
                : handleTaskClick(task)
            }
            onDismissTask={handleDismissTask}
          />
        ))}
      </div>
    </div>
  );
};
